// src/services/requisiciones/requisicionService.js
import { request } from "../../libs/http/http";

/**
 * Crear nueva requisición
 * @param {Object} data - { titulo, areaOrigen, notificacionOrigen, items[], observacionComercial }
 * @example items: [{ actividad, patron, tipoActividad, observacion }]
 */
export const crearRequisicion = (data = {}) => {
  return request('post', '/requisiciones', data);
}

/**
 * Listar requisiciones con filtros y paginación
 * @param {Object} params - Query params: { estado, areaOrigen, fechaDesde, fechaHasta, page, limit }
 */
export const listarRequisiciones = (params = {}) => {
  const queryString = new URLSearchParams(params).toString();
  const url = queryString ? `/requisiciones?${queryString}` : '/requisiciones';
  return request('get', url);
}

/**
 * Obtener detalle completo de una requisición
 * @param {string} id - ID de la requisición
 */
export const obtenerRequisicion = (id) => {
  return request('get', `/requisiciones/${id}`);
}

/**
 * Actualizar requisición (solo en estados Borrador o Enviada_Comercial)
 * @param {string} id - ID de la requisición
 * @param {Object} data - { titulo, items[], observacionComercial, etc. }
 */
export const actualizarRequisicion = (id, data = {}) => {
  return request('patch', `/requisiciones/${id}`, data);
}

/**
 * Cambiar estado de la requisición
 * @param {string} id - ID de la requisición
 * @param {Object} data - { estado, observacion }
 * @example estados: 'Borrador', 'Enviada_Comercial', 'Aprobada_Comercial', 'Enviada_Proveedor', 'Cotizacion_Recibida', 'Rechazada', 'Cerrada'
 */
export const cambiarEstadoRequisicion = (id, data = {}) => {
  return request('patch', `/requisiciones/${id}/estado`, data);
}

/**
 * Agregar proveedor a la requisición
 * @param {string} id - ID de la requisición
 * @param {Object} data - { proveedorId }
 */
export const agregarProveedorRequisicion = (id, data = {}) => {
  return request('post', `/requisiciones/${id}/proveedores`, data);
}

/**
 * Actualizar proveedor asignado (cambiar estadoEnvio o actualizar cotización)
 * @param {string} requisicionId - ID de la requisición
 * @param {string} proveedorAsignadoId - ID del subdocumento proveedorAsignado
 * @param {Object} data - { estadoEnvio?, cotizacion?: { archivo, correoEnvio, observacion } }
 * @example estadoEnvio: 'Pendiente', 'Enviado', 'Aprobado', 'Rechazado'
 * @note Al cambiar a 'Enviado' se envía automáticamente el email al proveedor
 */
export const actualizarProveedorRequisicion = (requisicionId, proveedorAsignadoId, data = {}) => {
  return request('patch', `/requisiciones/${requisicionId}/proveedores/${proveedorAsignadoId}`, data);
}

/**
 * Obtener requisición por token público (SIN AUTENTICACIÓN)
 * @param {string} token - Token UUID público del proveedor
 * @returns Datos de la requisición visible para el proveedor
 * @note Esta ruta NO requiere header Authorization
 */
export const obtenerRequisicionPublica = (token) => {
  return request('get', `/requisiciones/publica/${token}`);
}

/**
 * Proveedor envía cotización por token público (SIN AUTENTICACIÓN)
 * @param {string} token - Token UUID público del proveedor
 * @param {Object} data - { correoEnvio?, archivo?, observacion? }
 * @note Esta ruta NO requiere header Authorization
 * @note Debe proporcionar al menos correoEnvio o archivo
 */
export const responderRequisicionPublica = (token, data = {}) => {
  return request('patch', `/requisiciones/publica/${token}/respuesta`, data);
}

export const emitirOrdenCompraRequisicion = (id, archivoUrl) => {
  return request('post', `/requisiciones/${id}/orden-compra`, { archivoUrl });
}

export const aprobarOrdenCompraRequisicion = (id) => {
  return request('patch', `/requisiciones/${id}/orden-compra/aprobar`, {});
}

export const enviarOCProveedorRequisicion = (id) => {
  return request('post', `/requisiciones/${id}/orden-compra/enviar-proveedor`, {});
}

export const adjuntarCertificadoRequisicion = (id, proveedorAsignadoId, archivosUrls) => {
  return request('post', `/requisiciones/${id}/proveedores/${proveedorAsignadoId}/certificados`, { archivosUrls });
}

export const marcarCertificadosRecibidosRequisicion = (id) => {
  return request('patch', `/requisiciones/${id}/certificados/marcar-recibidos`, {});
}

export const responderCertificadosPublico = (token, archivoUrl) => {
  return request('patch', `/requisiciones/publica/${token}/certificados`, { archivoUrl });
}

// ============================================
// TIN-23: Licitación multi-proveedor
// ============================================

/**
 * Envío masivo de links a todos los proveedores pendientes.
 * Requiere mínimo 2 proveedores activos asignados.
 * @param {string} id - ID de la requisición
 * @param {object} [body]
 * @param {Record<string,string>} [body.correosOverride] - TIN-23 P1: { [proveedorAsignadoId]: correo }
 *        Si está presente, se usa ese correo en lugar del que tiene el proveedor en BD
 *        y se persiste en `proveedoresAsignados[].correoEnvioInvitacion`.
 * @returns {Promise<{ok:boolean, enviados:number, erroresEnvio:Array}>}
 */
export const enviarLinksProveedoresRequisicion = (id, body = {}) => {
  return request('post', `/requisiciones/${id}/proveedores/enviar-links`, body);
}

/**
 * Seleccionar proveedor ganador (cierra licitación, descarta a los demás).
 * Operación atómica e irreversible. Los descartados pierden acceso a su portal.
 * @param {string} id
 * @param {string} proveedorAsignadoId
 */
export const seleccionarGanadorRequisicion = (id, proveedorAsignadoId) => {
  return request('patch', `/requisiciones/${id}/proveedores/${proveedorAsignadoId}/seleccionar-ganador`, {});
}

/**
 * Proveedor acepta o rechaza la invitación desde el portal público (SIN AUTH).
 * @param {string} token
 * @param {boolean} aceptar
 */
export const responderInvitacionPublica = (token, aceptar) => {
  return request('patch', `/requisiciones/publica/${token}/respuesta-invitacion`, { aceptar });
}
